import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdDeliveryDining } from 'react-icons/md';

interface CartItem {
  id?: string;
  name?: string;
  price?: number;
  quantity?: number;
  [key: string]: any;
}

interface QuoteData {
  id: string;
  fee: number;
  currency: string;
  dropoff_eta: string;
}

interface DeliveryQuotePopupProps {
  show: boolean;
  onClose: () => void;
  quoteData: QuoteData | null;
  cartItems: CartItem[];
  subtotal: number;
  onPlaceOrder: () => void;
  orderLoading: boolean;
}

export const DeliveryQuotePopup: React.FC<DeliveryQuotePopupProps> = ({
  show,
  onClose,
  quoteData,
  cartItems,
  subtotal,
  onPlaceOrder,
  orderLoading,
}) => {
  if (!quoteData) return null;

  const deliveryFee = quoteData.fee / 100; // Convert cents to dollars
  const total = subtotal + deliveryFee;

  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
                <div className="flex items-center space-x-2">
                  <MdDeliveryDining className="w-6 h-6 text-red-500" />
                  <h2 className="text-xl font-bold text-gray-900">Delivery Quote</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <MdClose className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 space-y-4">
                {/* Order Items */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Order Items</h3>
                  <div className="space-y-2">
                    {cartItems.map((item, index) => (
                      <div key={item.item?.id || index} className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {item.quantity || 1}x {item.item?.name || 'Item'}
                        </span>
                        <span className="font-medium text-gray-900">
                          ${((item.totalPrice || 0) * (item.quantity || 1)).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200" />

                {/* Price Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span className="font-medium text-gray-900">${deliveryFee.toFixed(2)}</span>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200" />

                {/* Total */}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-red-500">${total.toFixed(2)}</span>
                </div>

                {/* Estimated Delivery Time */}
                {quoteData.dropoff_eta && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">Estimated Delivery:</span>{' '}
                      {new Date(quoteData.dropoff_eta).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
                <button
                  onClick={onPlaceOrder}
                  disabled={orderLoading}
                  className={`w-full bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center ${
                    orderLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {orderLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    'Continue'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
